import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiChevronDown, FiCheckCircle, FiTrendingUp, FiShield } from 'react-icons/fi';
import { useTranslation } from '../hooks/useTranslation';

const HeroSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const { t } = useTranslation();

  const features = [
    { icon: <FiCheckCircle />, text: t('hero.features.accurateTax') },
    { icon: <FiTrendingUp />, text: t('hero.features.financialGrowth') },
    { icon: <FiShield />, text: t('hero.features.compliance') }
  ];

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 py-20 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-block mb-6 px-4 py-2 bg-gradient-to-r from-[#A37A00] to-[#8A6800] rounded-full text-white text-sm font-semibold"
          >
            {t('hero.trustedBy')}
          </motion.div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="block text-gray-900 dark:text-white">{t('hero.elevateYour')}</span>
            <span className="block bg-gradient-to-r from-[#A37A00] to-[#8A6800] bg-clip-text text-transparent">
              {t('hero.financialSuccess')}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature, index) => (
              <motion.divu 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="flex items-center space-x-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm px-4 py-2 rounded-lg border border-amber-200 dark:border-amber-800/30"
              >
                <span className="text-[#A37A00] dark:text-amber-400">{feature.icon}</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{feature.text}</span>
              </motion.divu>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-gradient-to-r from-[#A37A00] to-[#8A6800] text-white rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-shadow"
            >
              {t('hero.cta.getStarted')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-800 dark:text-white rounded-lg font-semibold text-lg border border-gray-300 dark:border-gray-600"
            >
              {t('hero.cta.viewServices')}
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.7 }}
            className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto"
          >
            {[
              { value: '99%', label: t('hero.stats.satisfaction') },
              { value: '500+', label: t('hero.stats.businesses') },
              { value: '24/7', label: t('hero.stats.support') }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-[#A37A00] dark:text-amber-400">{stat.value}</div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <FiChevronDown size={24} className="text-gray-400" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;